import { useEffect, useMemo, useState } from "react";
import { Autocomplete, TextField, InputAdornment, Box, Typography, Avatar, CircularProgress, Button } from "@mui/material";
import { Search } from "@mui/icons-material";
import { colors } from "../../ui/colors";
import { TimeSheetSearchSuggestion, TimeSheetSearchTypeFilter } from "../interfaces/time-sheet.interfaces";
import { useTimeSheetSearchSuggestionsQuery } from "../hooks/useTimeSheetSearchSuggestionsQuery";

type Props = {
  value?: TimeSheetSearchSuggestion | null;
  onSearchSelect: (suggestion: TimeSheetSearchSuggestion | null) => void;
};

// Configuracion de los filtros junto al buscador.
type SearchTypeFilterOption = {
  value: TimeSheetSearchTypeFilter;
  label: string;
};

const searchTypeFilterOptions: SearchTypeFilterOption[] = [
  { value: "all", label: "All" },
  { value: "worker", label: "Workers" },
  { value: "client", label: "Clients" },
  { value: "job", label: "Jobs" },
];

// Cambia a true si necesitas probar el diseno sin back.
const USE_MOCK_SUGGESTIONS = false;

// Datos temporales del front. Luego esto se reemplaza por la respuesta del back.
const mockSuggestions: TimeSheetSearchSuggestion[] = [
  {
    type: "worker",
    label: "Graiden Booth",
    subtitle: "Worker",
    value: "worker-uid",
    query: { workerUid: "worker-uid" },
  },
  {
    type: "client",
    label: "Graiden Booth",
    subtitle: "Client",
    value: "client-uid",
    query: { clientUid: "client-uid" },
  },
  {
    type: "job",
    label: "room green",
    subtitle: "Job alias - Graiden Booth",
    value: "job-room-green",
    query: { jobAlias: "room green" },
  },
  {
    type: "job",
    label: "deep",
    subtitle: "Job alias - Graiden Booth",
    value: "job-deep",
    query: { jobAlias: "deep" },
  },
  {
    type: "job",
    label: "room big",
    subtitle: "Job alias - Juan Hernandez",
    value: "job-room-big",
    query: { jobAlias: "room big" },
  },
  {
    type: "job",
    label: "attic",
    subtitle: "Job alias - Juan Hernandez",
    value: "job-attic",
    query: { jobAlias: "attic" },
  },
  {
    type: "job",
    label: "Regular Cleaning",
    subtitle: "Job alias - Maria Lopez",
    value: "job-regular-cleaning",
    query: { jobAlias: "Regular Cleaning" },
  },
  {
    type: "client",
    label: "Juan Hernandez",
    subtitle: "Client",
    value: "client-juan-hernandez",
    query: { clientUid: "client-juan-hernandez" },
  },
  {
    type: "client",
    label: "Maria Lopez",
    subtitle: "Client",
    value: "client-maria-lopez",
    query: { clientUid: "client-maria-lopez" },
  },
  {
    type: "client",
    label: "North Star Offices",
    subtitle: "Client company",
    value: "client-north-star-offices",
    query: { clientUid: "client-north-star-offices" },
  },
  {
    type: "worker",
    label: "Juan Hernandez",
    subtitle: "Worker",
    value: "worker-juan-hernandez",
    query: { workerUid: "worker-juan-hernandez" },
  },
  {
    type: "worker",
    label: "Sofia Ramirez",
    subtitle: "Worker",
    value: "worker-sofia-ramirez",
    query: { workerUid: "worker-sofia-ramirez" },
  },
  {
    type: "worker",
    label: "Michael Chen",
    subtitle: "Worker",
    value: "worker-michael-chen",
    query: { workerUid: "worker-michael-chen" },
  },
];

type SearchTypeFilterButtonProps = {
  option: SearchTypeFilterOption;
  isSelected: boolean;
  onSelect: (value: TimeSheetSearchTypeFilter) => void;
};

// Boton individual de filtro: All, Workers, Clients o Jobs.
const SearchTypeFilterButton = ({ option, isSelected, onSelect }: SearchTypeFilterButtonProps) => (
  <Button
    type="button"
    onClick={() => onSelect(option.value)}
    variant={isSelected ? "contained" : "outlined"}
    sx={{
      minWidth: "auto",
      height: 30,
      px: 1.4,
      borderRadius: 2,
      borderColor: isSelected ? colors.orange.base : "#e2e8f0",
      bgcolor: isSelected ? colors.orange.base : "#fff",
      color: isSelected ? "#fff" : "var(--fx-surface-textsecondary, #64748B)",
      boxShadow: "none",
      fontSize: 11,
      fontWeight: 900,
      textTransform: "none",
      whiteSpace: "nowrap",
      "&:hover": {
        borderColor: colors.orange.base,
        bgcolor: isSelected ? colors.orange[600] : "var(--fx-colors-warning-50, #FFF7ED)",
        boxShadow: "none",
      },
    }}
  >
    {option.label}
  </Button>
);

type SearchTypeFilterButtonsProps = {
  value: TimeSheetSearchTypeFilter;
  onChange: (value: TimeSheetSearchTypeFilter) => void;
};

// Muestra los botones de filtro junto al buscador.
const SearchTypeFilterButtons = ({ value, onChange }: SearchTypeFilterButtonsProps) => (
  <Box display="flex" alignItems="center" gap={0.75} sx={{ flexShrink: 0 }}>
    {searchTypeFilterOptions.map((option) => (
      <SearchTypeFilterButton key={option.value} option={option} isSelected={value === option.value} onSelect={onChange} />
    ))}
  </Box>
);

const BardTimeSheet = ({ value = null, onSearchSelect }: Props) => {
  const [search, setSearch] = useState("");
  const [searchTypeFilter, setSearchTypeFilter] = useState<TimeSheetSearchTypeFilter>("all");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Espera un poco antes de consultar al back mientras el usuario escribe.
  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => window.clearTimeout(timeoutId);
  }, [search]);

  const { data: suggestionsResponse, isLoading } = useTimeSheetSearchSuggestionsQuery({
    search: debouncedSearch,
    type: searchTypeFilter,
  });

  // Usa el back por default; el mock queda listo para probar el diseno.
  const suggestions = useMemo(() => {
    if (USE_MOCK_SUGGESTIONS) return suggestionsResponse?.data ?? mockSuggestions;

    return suggestionsResponse?.data ?? [];
  }, [suggestionsResponse]);

  // Filtra sugerencias por tipo seleccionado y texto escrito.
  const filteredSuggestions = useMemo(() => {
    const term = debouncedSearch.trim().toLowerCase();

    return suggestions.filter((suggestion) => {
      const matchesType = searchTypeFilter === "all" || suggestion.type === searchTypeFilter;

      if (!matchesType) return false;
      if (!term) return true;

      const label = suggestion.label.toLowerCase();
      const subtitle = suggestion.subtitle?.toLowerCase() ?? "";
      const type = suggestion.type.toLowerCase();

      return label.includes(term) || subtitle.includes(term) || type.includes(term);
    });
  }, [debouncedSearch, searchTypeFilter, suggestions]);

  return (
    <Box display="flex" alignItems="center" gap={1} sx={{ width: "58%", minWidth: 520 }}>
      <SearchTypeFilterButtons
        value={searchTypeFilter}
        onChange={(newFilter) => {
          setSearchTypeFilter(newFilter);
          onSearchSelect(null);
        }}
      />

      {/* Autocomplete mixto para workers, clientes y jobs. */}
      <Autocomplete
        value={value}
        options={filteredSuggestions}
        loading={isLoading}
        disablePortal
        sx={{
          flex: 1,
          minWidth: 260,
          "& .MuiInputLabel-root.Mui-focused": {
            color: colors.orange.base,
          },

          "& .MuiOutlinedInput-root": {
            fontSize: 15,
            height: 36,
            borderRadius: "8px",
            bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
            transition: "all 0.2s",

            "& fieldset": {
              borderColor: "var(--fx-surface-border, #E2E8F0)",
            },

            "&:hover fieldset": {
              borderColor: "var(--fx-surface-borderstrong, #CBD5E1)",
            },

            "&.Mui-focused": {
              bgcolor: "var(--fx-surface-base, #FFFFFF)",
              "& fieldset": {
                borderColor: colors.orange.base,
                borderWidth: "2px",
              },
            },
          },

          "& .MuiAutocomplete-popupIndicator": {
            color: "var(--fx-surface-texttertiary, #94A3B8)",
            "&:hover": { color: colors.orange.base },
          },
        }}
        getOptionLabel={(option: TimeSheetSearchSuggestion) => option.label}
        isOptionEqualToValue={(option: TimeSheetSearchSuggestion, selected: TimeSheetSearchSuggestion) => option.value === selected.value}
        // Manda la sugerencia seleccionada de regreso a TimeSheet.tsx.
        onChange={(_, newValue) => onSearchSelect(newValue)}
        inputValue={search}
        onInputChange={(_, newInputValue) => setSearch(newInputValue)}
        noOptionsText={debouncedSearch ? "No results match your search" : "No suggestions available"}
        renderInput={(params) => {
          const { InputProps, ...restParams } = params;

          return (
            <TextField
              {...restParams}
              placeholder="Search worker, client, or job..."
              InputProps={{
                ...InputProps,
                startAdornment: (
                  <>
                    <InputAdornment position="start">
                      <Search sx={{ color: "text.secondary", fontSize: 20, ml: 1 }} />
                    </InputAdornment>
                    {InputProps.startAdornment}
                  </>
                ),
                endAdornment: (
                  <>
                    {isLoading ? <CircularProgress color="inherit" size={16} /> : null}
                    {InputProps.endAdornment}
                  </>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  bgcolor: "var(--fx-surface-base, #FFFFFF)",
                  height: 40,
                  display: "flex",
                  alignItems: "center",
                  pl: "10px !important",
                  "& input": {
                    fontSize: 14,
                    py: 0,
                  },
                  "& fieldset": { borderColor: "divider" },
                  "&:hover fieldset": { borderColor: "divider" },
                  "&.Mui-focused fieldset": { borderWidth: 1 },
                },
              }}
            />
          );
        }}
        renderOption={(props, option) => (
          <Box
            component="li"
            {...props}
            key={option.value}
            sx={{
              px: 2,
              py: 1,
              display: "flex",
              alignItems: "center",
              borderBottom: "1px solid var(--fx-surface-border, #E2E8F0)",
              "&:last-child": { borderBottom: "none" },
            }}
          >
            <Avatar
              sx={{
                width: 28,
                height: 28,
                mr: 1.5,
                fontSize: 10,
                fontWeight: 900,
                bgcolor: option.type === "worker" ? "var(--fx-colors-info-50, #EFF6FF)" : option.type === "client" ? "var(--fx-colors-success-100, #D1FAE5)" : "var(--fx-colors-warning-100, #FFEDD5)",
                color: option.type === "worker" ? "#0369a1" : option.type === "client" ? "#15803d" : "#c2410c",
              }}
            >
              {option.type === "worker" ? "W" : option.type === "client" ? "C" : "J"}
            </Avatar>

            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: 12, color: "var(--fx-surface-text, #0F172A)" }}>{option.label}</Typography>

              <Typography sx={{ fontSize: 10, color: "var(--fx-surface-textsecondary, #64748B)" }}>{option.subtitle}</Typography>
            </Box>
          </Box>
        )}
      />
    </Box>
  );
};

export default BardTimeSheet;
